import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { formatPhoneNumber } from '@/types/PhoneNumberDto.utils.ts';
import { CostLineType } from '@/features/dashboard/bookings/types/BookingBillDto.ts';
import {
  BillCatalog,
  BillLine,
  BillLineRef,
  billFieldsOf,
  findCatalogVariant,
  hasBillField,
  BillSection,
} from '@/features/dashboard/bookingChanges/types/BookingBill.ts';
import { costRowKey } from '@/features/dashboard/bookingChanges/types/ChangePreview.ts';
import { BillField } from '@/features/dashboard/bookingChanges/types/dto/BillField.ts';
import { ClientBillField } from '@/features/dashboard/bookingChanges/types/dto/ClientBillField.ts';
import { isAddressLine } from '@/features/dashboard/bookingChanges/types/dto/ClientBillField.utils.ts';
import { ParticipantBillField } from '@/features/dashboard/bookingChanges/types/dto/ParticipantBillField.ts';
import {
  ChangeItemDto,
  ChangeItemType,
  ClientDataChangeItemDto,
  isChangeItemOfType,
  ManualLineChangeItemDto,
  ParticipantDataChangeItemDto,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import { PriceSource } from '@/features/dashboard/bookingChanges/types/dto/PriceSource.ts';
import {
  InsuranceChangeOp,
  OptionChangeOp,
  TransportChangeOp,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeOps.ts';
import { formatDate } from '@/features/dashboard/bookingChanges/utils/formatters.ts';

/**
 * Spięcie pozycji paczki z wierszami rachunku.
 *
 * Pozycja paczki niesie UUID-y i stan docelowy, wiersz rachunku — stan bieżący
 * i etykietę. Dopiero razem dają wiersz widoku „przed → po”.
 *
 * **Kwot tu nie ma.** Nakładanie wartości „przed” na „po” jest jedyną rzeczą,
 * którą frontowi wolno robić z paczką; delty i kwoty docelowe przychodzą
 * z `GET …/changes/{changeId}/preview` (`types/ChangePreview.ts`).
 */

/** Czy pozycja paczki dotyczy tego wiersza rachunku? */
export const matchesLine = (
  item: ChangeItemDto,
  line: BillLine,
  participantId: string | null,
): boolean => {
  const { ref } = line;
  if (!ref) {
    return false;
  }

  switch (ref.kind) {
    case ChangeItemType.BASE_PRICE:
      return (
        item.type === ChangeItemType.BASE_PRICE &&
        item.participantId === participantId
      );
    case ChangeItemType.OPTION:
      return (
        item.type === ChangeItemType.OPTION &&
        item.participantId === participantId &&
        item.tourOptionId === ref.targetId
      );
    case ChangeItemType.TRANSPORT:
      return (
        item.type === ChangeItemType.TRANSPORT &&
        item.participantId === participantId
      );
    case ChangeItemType.INSURANCE:
      return (
        item.type === ChangeItemType.INSURANCE &&
        item.participantId === participantId
      );
    case ChangeItemType.CUSTOM_VALUES:
      return (
        item.type === ChangeItemType.CUSTOM_VALUES &&
        item.participantId === participantId &&
        !!ref.targetId &&
        !!item.newCustomValues &&
        ref.targetId in item.newCustomValues
      );
    case ChangeItemType.PARTICIPANT_DATA:
      if (
        item.type !== ChangeItemType.PARTICIPANT_DATA ||
        item.participantId !== participantId
      ) {
        return false;
      }
      return billFieldsOf(ref).some((field) =>
        participantFieldChanged(field, item),
      );
    case ChangeItemType.CLIENT_DATA:
      if (item.type !== ChangeItemType.CLIENT_DATA) {
        return false;
      }
      return billFieldsOf(ref).some((field) => clientFieldChanged(field, item));
    case ChangeItemType.MANUAL_LINE:
      // Wolna linia nie ma odpowiednika w cenniku ani w danych osoby, więc
      // jedynym spoiwem wiersza z pozycją jest identyfikator samej pozycji.
      return (
        item.type === ChangeItemType.MANUAL_LINE &&
        item.participantId === participantId &&
        item.id === ref.targetId
      );
    default:
      return false;
  }
};

/**
 * Czy pozycja rusza dane pole uczestnika.
 *
 * Wiersz może opisywać kilka pól naraz („Imię i nazwisko”), więc pytamy o każde
 * osobno i wystarczy, że pozycja rusza którekolwiek.
 */
const participantFieldChanged = (
  field: BillField,
  item: ParticipantDataChangeItemDto,
): boolean => {
  switch (field) {
    case ParticipantBillField.FIRST_NAME:
      return item.newFirstName !== null;
    case ParticipantBillField.LAST_NAME:
      return item.newLastName !== null;
    case ParticipantBillField.BIRTH_DATE:
      return item.newBirthDate !== null;
    default:
      return false;
  }
};

const clientFieldChanged = (
  field: BillField,
  item: ClientDataChangeItemDto,
): boolean => {
  switch (field) {
    case ClientBillField.FIRST_NAME:
      return item.newFirstName !== null;
    case ClientBillField.LAST_NAME:
      return item.newLastName !== null;
    case ClientBillField.EMAIL:
      return item.newEmail !== null;
    case ClientBillField.PHONE:
      return item.newPhoneNumber !== null;
    // Adres jest jednym polem pozycji paczki i trzema polami wiersza —
    // zmiana dotyczy całej trójki naraz.
    case ClientBillField.STREET:
    case ClientBillField.POSTAL_CODE:
    case ClientBillField.CITY:
      return item.newAddress !== null;
    case ClientBillField.BIRTH_DATE:
      return item.newBirthDate !== null;
    default:
      return false;
  }
};

/** Pozycja paczki dotycząca danego wiersza (albo `undefined`). */
export const findItemForLine = (
  items: ChangeItemDto[],
  line: BillLine,
  participantId: string | null,
): ChangeItemDto | undefined =>
  items.find((item) => matchesLine(item, line, participantId));

/**
 * Wszystkie pozycje paczki dotyczące wiersza.
 *
 * Wiersz jednowartościowy ma ich najwyżej jedną, ale wybór wielokrotny
 * („Wycieczki fakultatywne”) rozkłada się na pozycję `ADD`/`REMOVE` **na
 * wariant** — `OptionChangeItemDto` niesie jeden `newVariantId`.
 */
export const findItemsForLine = (
  items: ChangeItemDto[],
  line: BillLine,
  participantId: string | null,
): ChangeItemDto[] =>
  items.filter((item) => matchesLine(item, line, participantId));

/**
 * Warianty wiersza wielowartościowego po uwzględnieniu pozycji paczki;
 * `null` dla wierszy, które wielowartościowe nie są.
 */
export const resolveNewVariantIds = (
  line: BillLine,
  items: ChangeItemDto[],
): string[] | null => {
  const current = line.ref?.currentVariantIds;
  if (!current) {
    return null;
  }

  return items.reduce<string[]>((selected, item) => {
    if (item.type !== ChangeItemType.OPTION) {
      return selected;
    }
    const without = item.removedVariantId
      ? selected.filter((variantId) => variantId !== item.removedVariantId)
      : selected;

    return item.newVariantId && !without.includes(item.newVariantId)
      ? [...without, item.newVariantId]
      : without;
  }, current);
};

/**
 * Napis wiersza „Imię i nazwisko” po zmianie. Pozycja paczki może nieść tylko
 * jedno z pól (drugie zostaje bez zmian), więc puste człony wypadają.
 */
const personNameText = (
  firstName: string | null,
  lastName: string | null,
): string | null => [firstName, lastName].filter(Boolean).join(' ') || null;

const variantName = (
  catalog: BillCatalog,
  ref: BillLineRef | null,
  variantId: string | null,
): string | null => findCatalogVariant(catalog, ref, variantId)?.name ?? null;

const customValueToText = (
  value: string | number | boolean | null,
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'boolean') {
    return value ? 'Tak' : 'Nie';
  }
  return String(value);
};

/** Wartość „Nie wybrano” — jedyny tekst pustego wyboru w całym module. */
export const NO_SELECTION_LABEL = 'Nie wybrano';

/** Nazwy wybranych wariantów, w kolejności ich wyboru. */
export const resolveVariantNames = (
  variantIds: string[],
  catalog: BillCatalog,
  ref: BillLineRef | null,
): string[] =>
  variantIds
    .map((variantId) => variantName(catalog, ref, variantId))
    .filter((name): name is string => !!name);

/**
 * Tekstowa wartość wiersza po uwzględnieniu pozycji paczki.
 *
 * Nazwy wariantów pochodzą ze słownika `catalog` — pozycja paczki niesie sam
 * UUID.
 */
export const resolveNewValue = (
  line: BillLine,
  items: ChangeItemDto[],
  catalog: BillCatalog,
): string | null => {
  const variantIds = resolveNewVariantIds(line, items);
  if (variantIds) {
    const names = resolveVariantNames(variantIds, catalog, line.ref);
    return names.length > 0 ? names.join(', ') : NO_SELECTION_LABEL;
  }

  const [item] = items;
  if (!item) {
    return null;
  }

  return resolveSingleValue(line, item, catalog);
};

const resolveSingleValue = (
  line: BillLine,
  item: ChangeItemDto,
  catalog: BillCatalog,
): string | null => {
  switch (item.type) {
    case ChangeItemType.OPTION:
      return item.op === OptionChangeOp.REMOVE
        ? NO_SELECTION_LABEL
        : (variantName(catalog, line.ref, item.newVariantId) ??
            NO_SELECTION_LABEL);
    case ChangeItemType.TRANSPORT:
      return item.op === TransportChangeOp.CLEAR
        ? NO_SELECTION_LABEL
        : (variantName(catalog, line.ref, item.newVariantId) ??
            NO_SELECTION_LABEL);
    case ChangeItemType.INSURANCE:
      return item.op === InsuranceChangeOp.CLEAR
        ? NO_SELECTION_LABEL
        : (variantName(catalog, line.ref, item.newInsuranceId) ??
            NO_SELECTION_LABEL);
    case ChangeItemType.PARTICIPANT_DATA:
      if (hasBillField(line.ref, ParticipantBillField.BIRTH_DATE)) {
        return item.newBirthDate ? formatDate(item.newBirthDate) : null;
      }
      return personNameText(item.newFirstName, item.newLastName);
    case ChangeItemType.CLIENT_DATA:
      if (hasBillField(line.ref, ClientBillField.EMAIL)) {
        return item.newEmail;
      }
      if (hasBillField(line.ref, ClientBillField.PHONE)) {
        return item.newPhoneNumber
          ? formatPhoneNumber(item.newPhoneNumber)
          : null;
      }
      if (hasBillField(line.ref, ClientBillField.BIRTH_DATE)) {
        return item.newBirthDate ? formatDate(item.newBirthDate) : null;
      }
      if (isAddressLine(line.ref)) {
        return item.newAddress
          ? `${item.newAddress.street}, ${item.newAddress.postalCode} ${item.newAddress.city}`
          : null;
      }
      return personNameText(item.newFirstName, item.newLastName);
    case ChangeItemType.CUSTOM_VALUES: {
      const targetId = line.ref?.targetId;
      const value = targetId ? item.newCustomValues?.[targetId] : undefined;
      if (!value) {
        return null;
      }
      // Wartość pola listy rozwijanej to UUID opcji — etykietę zna wyłącznie
      // definicja pola, czyli po tej stronie katalog.
      return value.type === FieldType.DROPDOWN
        ? (variantName(catalog, line.ref, value.value) ?? NO_SELECTION_LABEL)
        : customValueToText(value.value);
    }
    // Opis pozycji ręcznej jest etykietą wiersza (`resolveNewLabel`), więc
    // w kolumnie wartości zostaje uzasadnienie kwoty — tak samo jak przy
    // zniżkach rezerwacji, które w tym miejscu tłumaczą, skąd się wzięły.
    case ChangeItemType.MANUAL_LINE:
      return item.priceNote;
    default:
      return null;
  }
};

/**
 * Etykieta wiersza po uwzględnieniu pozycji paczki; `null` znaczy „bez zmian”.
 *
 * Rusza ją wyłącznie pozycja ręczna: jako jedyna nie ma nazwy w rachunku ani
 * w cenniku — nadaje ją operator w opisie pozycji.
 */
export const resolveNewLabel = (items: ChangeItemDto[]): string | null => {
  const [item] = items;
  return item?.type === ChangeItemType.MANUAL_LINE ? item.description : null;
};

/**
 * Nowy zestaw chipów wiersza wielokrotnego wyboru.
 *
 * Warianty cennika składamy z identyfikatorów (`ref.currentVariantIds` plus
 * pozycje paczki), a wartości niestandardowe przychodzą jako jeden string,
 * więc te rozbijamy po przecinku.
 */
export const resolveNewChips = (
  line: BillLine,
  items: ChangeItemDto[],
  catalog: BillCatalog,
): string[] | null => {
  if (!line.chips) {
    return null;
  }

  const variantIds = resolveNewVariantIds(line, items);
  if (variantIds) {
    return resolveVariantNames(variantIds, catalog, line.ref);
  }

  const value = resolveNewValue(line, items, catalog);
  if (value === null) {
    return null;
  }
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
};

/**
 * Wiersz rachunku odwzorowujący pozycję ręczną ze szkicu.
 *
 * Pozycja ręczna jako jedyna nie ma odpowiednika w rachunku — powstaje razem
 * z paczką, więc stanu „przed” po prostu nie ma. Wiersz jest więc pusty:
 * pusta lista kwot znaczy „przed zmianą tej kwoty tu nie było”, a kwotę wnosi
 * dopiero podgląd paczki, przez `costKey` — pozycja dodawana nie ma `id`,
 * więc nie ma czego złączyć.
 */
export const manualBillLine = (
  id: string,
  label: string,
  participantId: string | null,
): BillLine => ({
  id,
  ref: {
    kind: ChangeItemType.MANUAL_LINE,
    targetId: id,
    fields: null,
    currentVariantId: null,
    currentVariantIds: null,
  },
  label,
  type: FieldType.MONEY_VALUE,
  costLineIds: [],
  costKey: costRowKey(participantId, CostLineType.MANUAL, label),
  value: null,
  chips: null,
  amount: [],
  amountLabel: null,
  amountMuted: false,
  muted: false,
  locked: false,
  manual: true,
  priceOverridden: false,
  priceNote: null,
});

/** Wiersz rachunku odwzorowujący pozycję ręczną, która jest już w szkicu. */
export const manualLineOf = (item: ManualLineChangeItemDto): BillLine =>
  manualBillLine(item.id, item.description, item.participantId);

/**
 * Sekcja przyjmująca pozycje ręczne — pierwsza z `allowManualLines`.
 *
 * Pozycja paczki nie niesie identyfikatora sekcji (encja go nie ma), więc
 * miejsce wiersza wynika z samego rachunku: uczestnik ma „Pozycje ręczne”,
 * rezerwacja — „Zniżki i korekty”. Sekcja bez kolumny kwot odpada z definicji:
 * pozycja ręczna *jest* kwotą, więc nie miałaby się gdzie pokazać.
 */
export const findManualLineSection = (
  sections: BillSection[],
): BillSection | undefined =>
  sections.find((section) => section.allowManualLines && section.withAmounts);

/** Pozycje ręczne szkicu dla danej osoby (`null` = cała rezerwacja). */
export const manualLineItems = (
  items: ChangeItemDto[],
  participantId: string | null,
): ManualLineChangeItemDto[] =>
  items
    .filter(isChangeItemOfType(ChangeItemType.MANUAL_LINE))
    .filter((item) => item.participantId === participantId);

/**
 * Opisy zniżek całej rezerwacji — tylko tych backend rozkłada na uczestników
 * (`participantId === null` i kwota ujemna). Dopłata całej rezerwacji zostaje
 * w grupie `null`, więc od uczestników nie ma czego odejmować.
 */
export const bookingWideDiscountDescriptions = (
  items: ChangeItemDto[],
): string[] =>
  manualLineItems(items, null)
    .filter((item) => item.priceOverride.amount < 0)
    .map((item) => item.description);

/**
 * Sekcje rachunku wraz z wierszami, które **dopisuje** szkic.
 *
 * Wszystkie pozostałe rodzaje pozycji dekorują wiersz, który w rachunku już
 * jest; pozycja ręczna sama go tworzy. Doklejamy więc te, których rachunek
 * jeszcze nie zna — pozycja wprowadzona wcześniejszą paczką ma już własny
 * wiersz i drugi raz go nie dostaje.
 *
 * Funkcja jest wspólna dla mappera widoku, liczenia delt i mocka wprowadzenia
 * paczki, żeby wiersz nie powstawał w każdym z tych miejsc osobno.
 */
export const sectionsWithDraftLines = (
  sections: BillSection[],
  items: ChangeItemDto[],
  participantId: string | null,
): BillSection[] => {
  const target = findManualLineSection(sections);
  if (!target) {
    return sections;
  }

  const known = new Set(
    sections
      .flatMap((section) => section.lines)
      .flatMap((line) =>
        line.ref?.kind === ChangeItemType.MANUAL_LINE
          ? [line.ref.targetId]
          : [],
      ),
  );
  const added = manualLineItems(items, participantId)
    .filter((item) => !known.has(item.id))
    .map(manualLineOf);

  if (added.length === 0) {
    return sections;
  }

  return sections.map((section) =>
    section.id === target.id
      ? { ...section, lines: [...section.lines, ...added] }
      : section,
  );
};

/**
 * Ile pozycji paczki dotyczy danej osoby (`null` = osoba zapisująca).
 *
 * Pozycja ręczna bez uczestnika też ma `participantId === null`, ale nie jest
 * zmianą danych zapisującego — należy do rezerwacji, więc do jego licznika nie
 * wchodzi. Stąd pytanie o typ pozycji, a nie o samo `participantId`.
 */
export const countItemsForPerson = (
  items: ChangeItemDto[],
  participantId: string | null,
): number =>
  items.filter((item) =>
    participantId === null
      ? item.type === ChangeItemType.CLIENT_DATA
      : item.participantId === participantId,
  ).length;
